/**
 * Auth app : code email (OTP) puis JWT.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import type { User } from '@/types/auth'
import * as authService from '@/services/auth'
import { setSentryUser } from '@/lib/sentry'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  /** Clerk connecté, échange JWT app en cours — ne pas renvoyer vers /login */
  isClerkSyncing: boolean
  clerkSyncError: string | null
  login: (email: string, otpCode: string) => Promise<void>
  requestOTP: (email: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

function AuthProviderWithoutClerk({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasAppToken, setHasAppToken] = useState(() => authService.isAuthenticated())

  // Rattache les erreurs Sentry à l'utilisateur (identifiant et rôle uniquement)
  useEffect(() => {
    setSentryUser(user ? { id: user.id, role: user.role } : null)
  }, [user])

  useEffect(() => {
    const loadUser = async () => {
      if (authService.isAuthenticated()) {
        try {
          setUser(await authService.fetchCurrentUser())
          setHasAppToken(true)
        } catch {
          setUser(authService.getCurrentUserFromStorage())
          setHasAppToken(!!authService.getAuthToken())
        }
      } else {
        setUser(null)
        setHasAppToken(false)
      }
      setIsLoading(false)
    }
    void loadUser()
  }, [])

  const value: AuthContextType = {
    user,
    isAuthenticated: hasAppToken && authService.isAuthenticated(),
    isLoading,
    isClerkSyncing: false,
    clerkSyncError: null,
    login: async (email, otpCode) => {
      const response = await authService.verifyOTP(email, otpCode)
      setUser(response.user)
      setHasAppToken(true)
    },
    requestOTP: async (email) => {
      await authService.requestOTP(email)
    },
    logout: async () => {
      await authService.logout()
      setUser(null)
      setHasAppToken(false)
    },
    refreshUser: async () => {
      if (authService.isAuthenticated()) {
        try {
          setUser(await authService.fetchCurrentUser())
        } catch {
          setUser(authService.getCurrentUserFromStorage())
        }
      }
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return <AuthProviderWithoutClerk>{children}</AuthProviderWithoutClerk>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
